/**
 * Внесение расхода: сумма → категория → готово.
 *
 * Порядок именно такой: няня помнит сумму («450 за такси»), а не категорию.
 * Название подставляется само, чек — по желанию. Весь смысл фичи в том,
 * чтобы внести расход быстрее, чем передумать.
 */

import { useState } from 'react'

import type { ExpenseCategory } from '@/shared/api/day'
import { useCreateExpense } from '@/shared/api/day'
import { uploadPhoto } from '@/features/media/upload'
import { Button } from '@/shared/ui/Button'
import { Icon } from '@/shared/ui/Icon'
import { Sheet } from '@/shared/ui/Sheet'
import { Field, MoneyInput, TextInput } from '@/shared/ui/fields'
import { expenseIcon } from '@/shared/ui/icon-map'
import { ErrorBanner } from '@/shared/ui/states'
import { toast } from '@/shared/ui/toast'
import { haptic } from '@/shared/telegram'

// Значок категории здесь не хранится: он берётся из общей карты `expenseIcon`,
// чтобы плитка выбора и уже внесённый расход выглядели одинаково.
const CATEGORIES: { value: ExpenseCategory; label: string }[] = [
  { value: 'food', label: 'Еда' },
  { value: 'transport', label: 'Проезд' },
  { value: 'activity', label: 'Кружок' },
  { value: 'pharmacy', label: 'Аптека' },
  { value: 'household', label: 'Хозяйств.' },
  { value: 'other', label: 'Другое' },
]

export function ExpenseSheet({
  open,
  familyId,
  currency,
  onClose,
}: {
  open: boolean
  familyId: number
  currency: string
  onClose: () => void
}) {
  const [amount, setAmount] = useState('')
  const [category, setCategory] = useState<ExpenseCategory | null>(null)
  const [title, setTitle] = useState('')
  const [mediaIds, setMediaIds] = useState<number[]>([])
  const [uploading, setUploading] = useState(false)

  const reset = () => {
    setAmount('')
    setCategory(null)
    setTitle('')
    setMediaIds([])
  }

  const create = useCreateExpense(familyId, {
    onSuccess: (expense) => {
      haptic('success')
      toast.success(
        expense.status === 'approved'
          ? 'Расход добавлен и сразу одобрен'
          : 'Расход добавлен — придёт в расчёте',
      )
      reset()
      onClose()
    },
  })

  const canSubmit = Boolean(amount) && Number(amount) > 0 && category !== null

  return (
    <Sheet
      open={open}
      onClose={() => {
        reset()
        onClose()
      }}
      title="Расход"
      footer={
        <Button
          block
          size="lg"
          loading={create.isPending}
          disabled={!canSubmit}
          onClick={() =>
            category &&
            create.mutate({
              category,
              amount,
              title: title.trim() || undefined,
              media_ids: mediaIds,
            })
          }
        >
          Добавить
        </Button>
      }
    >
      {create.error && <ErrorBanner message={create.error.message} />}

      <Field label="Сколько потратили">
        <MoneyInput
          value={amount}
          onValueChange={setAmount}
          currency={currency === 'RUB' ? '₽' : currency}
        />
      </Field>

      <div className="mt-4">
        <span className="text-text-muted mb-1.5 block px-1 text-caption font-medium">
          На что
        </span>
        <div className="grid grid-cols-3 gap-2">
          {CATEGORIES.map((item) => (
            <button
              key={item.value}
              type="button"
              onClick={() => {
                haptic('select')
                setCategory(item.value)
              }}
              className={
                'press flex flex-col items-center gap-1 rounded-[var(--radius-tile)] py-3 ' +
                (category === item.value
                  ? 'bg-accent-soft text-accent'
                  : 'bg-surface-muted text-text-muted')
              }
            >
              <Icon name={expenseIcon(item.value)} size={21} />
              <span className="text-[11px]">{item.label}</span>
            </button>
          ))}
        </div>
      </div>

      {category && (
        <div className="mt-4">
          <Field label="Название" hint="Можно не заполнять — подставим по категории">
            <TextInput
              value={title}
              onChange={(event) => setTitle(event.target.value)}
              placeholder={CATEGORIES.find((item) => item.value === category)?.label}
              maxLength={64}
            />
          </Field>
        </div>
      )}

      <div className="mt-4">
        <label className="press bg-surface-muted text-text-muted flex h-12 w-full items-center justify-center gap-2 rounded-[var(--radius-button)] text-note">
          {uploading ? (
            'Загружаем…'
          ) : mediaIds.length ? (
            `Чек добавлен (${mediaIds.length})`
          ) : (
            // Скрепка отдельным элементом, а не в строке: иначе она попадёт
            // в озвучку кнопки вместе с подписью.
            <>
              <Icon name="action-attach-receipt" size={20} />
              Добавить чек
            </>
          )}
          <input
            type="file"
            accept="image/*"
            capture="environment"
            className="hidden"
            disabled={uploading || mediaIds.length >= 3}
            onChange={async (event) => {
              const file = event.target.files?.[0]
              if (!file) return
              setUploading(true)
              try {
                const media = await uploadPhoto(file)
                setMediaIds((current) => [...current, media.id])
              } catch {
                toast.error('Фото не загрузилось')
              } finally {
                setUploading(false)
                event.target.value = ''
              }
            }}
          />
        </label>
        <p className="text-text-muted mt-1.5 px-1 text-caption leading-snug">
          Чек не обязателен, но с ним обоим спокойнее.
        </p>
      </div>
    </Sheet>
  )
}
